import { type ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Funnel,
  FunnelChart,
  LabelList,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn } from '../../utils/cn';

export type ChartKind = 'line' | 'bar' | 'area' | 'donut' | 'funnel';

export interface ChartSeries {
  readonly key: string;
  readonly label?: string;
  /** Override the per-palette accent for this series — usually leave blank. */
  readonly colorVar?: string;
}

export interface ChartProps {
  readonly kind: ChartKind;
  /** Array of rows. Each row must contain the `xKey` field + every series.key. */
  readonly data: readonly Record<string, unknown>[];
  /** Field name that drives the X axis (or pie label for donut/funnel). */
  readonly xKey: string;
  readonly series: readonly ChartSeries[];
  readonly height?: number;
  readonly showLegend?: boolean;
  readonly showGrid?: boolean;
  readonly className?: string;
  /** Optional title rendered above the chart. */
  readonly title?: ReactNode;
  /** `bar` / `area` only: stack the series on one another instead of side by side. */
  readonly stacked?: boolean;
  /** Formats values on the Y axis and in the tooltip (e.g. `12.5 ms`). */
  readonly valueFormatter?: (value: number) => string;
  /** Formats X-axis ticks and the tooltip label (e.g. an ISO time → `14:05`). */
  readonly xTickFormatter?: (value: string) => string;
  /**
   * Cartesian kinds: label exactly these X values (all of them), instead of
   * letting Recharts pick — e.g. clean time boundaries on a dense series.
   */
  readonly xTicks?: readonly string[];
  /** Cartesian kinds: hide the X axis (stacked "lane" charts label only the last one). */
  readonly showXAxis?: boolean;
  /**
   * Cartesian kinds: `'log'` plots a base-10 log Y axis with power-of-ten ticks
   * (latency percentiles); values ≤ 0 are left out as gaps.
   */
  readonly yScale?: 'linear' | 'log';
  /** Cartesian kinds: approximate number of Y ticks (linear scale). */
  readonly yTickCount?: number;
  /** Cartesian kinds: fixed Y-axis width in px, so stacked charts line up. */
  readonly yAxisWidth?: number;
  /** Cartesian kinds: charts sharing an id share one hover crosshair. */
  readonly syncId?: string;
  /** Cartesian kinds: `false` keeps the crosshair but drops the floating tooltip box. */
  readonly showTooltip?: boolean;
  /** Cartesian kinds: the hovered data index, `null` when the pointer leaves. */
  readonly onActiveIndexChange?: (index: number | null) => void;
  /** Plot margin in px (Recharts' default when omitted). */
  readonly margin?: {
    readonly top?: number;
    readonly right?: number;
    readonly bottom?: number;
    readonly left?: number;
  };
}

/** Power-of-ten domain + ticks covering every positive value of the series. */
export function logScaleTicks(
  data: readonly Record<string, unknown>[],
  keys: readonly string[],
): { readonly domain: [number, number]; readonly ticks: number[] } | null {
  let min = Infinity;
  let max = -Infinity;
  for (const row of data) {
    for (const key of keys) {
      const v = row[key];
      if (typeof v === 'number' && v > 0) {
        min = Math.min(min, v);
        max = Math.max(max, v);
      }
    }
  }
  if (!Number.isFinite(min)) return null;
  const lo = Math.floor(Math.log10(min));
  const hi = Math.max(lo + 1, Math.ceil(Math.log10(max)));
  const ticks: number[] = [];
  for (let e = lo; e <= hi; e += 1) ticks.push(10 ** e);
  return { domain: [10 ** lo, 10 ** hi], ticks };
}

/**
 * BraveMove Chart wrapper — Recharts under the hood, styled from design tokens
 * via CSS variables. Rendering is responsive (fills parent width); pass an
 * explicit `height` to control vertical size.
 *
 * Token-driven palette: every series draws from `--color-accent`,
 * `--color-info`, `--color-success`, etc. (a 5-token rotation). To force a
 * specific colour, pass `series[i].colorVar = '--color-warning'`.
 *
 * Geo and Heatmap variants are NOT in v1 — they need different libs
 * (react-simple-maps + a calendar heatmap). They are placeholder-tracked in
 * docs/design/HANDOFF.md Section 7.
 */
export function Chart({
  kind,
  data,
  xKey,
  series,
  height = 280,
  showLegend = false,
  showGrid = true,
  className,
  title,
  stacked = false,
  valueFormatter,
  xTickFormatter,
  xTicks,
  showXAxis = true,
  yScale = 'linear',
  yTickCount,
  yAxisWidth,
  syncId,
  showTooltip = true,
  onActiveIndexChange,
  margin,
}: ChartProps) {
  const palette = [
    '--color-accent',
    '--color-info',
    '--color-success',
    '--color-warning',
    '--color-secondary',
  ];

  const colourFor = (i: number, override?: string): string =>
    `var(${override ?? palette[i % palette.length]})`;

  return (
    <div className={cn('w-full', className)}>
      {title ? (
        <div className="text-[12px] font-bold uppercase tracking-wider text-ink-3 font-mono mb-2">
          {title}
        </div>
      ) : null}
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );

  function renderChart() {
    const axisStyle = {
      stroke: 'var(--color-ink-3)',
      fontSize: 11,
      fontFamily: 'var(--font-mono)',
    } as const;
    const gridStroke = 'var(--color-hairline)';
    const tooltipStyle = {
      backgroundColor: 'var(--color-surface)',
      borderColor: 'var(--color-hairline-2)',
      borderRadius: 8,
      fontSize: 12,
    } as const;
    const cursorStroke = 'var(--color-hairline-2)';
    const xAxisFormat = xTickFormatter
      ? { tickFormatter: (v: unknown) => xTickFormatter(String(v)) }
      : {};
    const yAxisFormat = valueFormatter
      ? { tickFormatter: (v: unknown) => valueFormatter(Number(v)) }
      : {};
    const tooltipFormat = {
      ...(valueFormatter ? { formatter: (v: unknown) => valueFormatter(Number(v)) } : {}),
      ...(xTickFormatter ? { labelFormatter: (l: unknown) => xTickFormatter(String(l)) } : {}),
    };
    const stackId = stacked ? { stackId: 'stack' } : {};
    // Cartesian-only options (additive; omitted props keep Recharts' defaults).
    const seriesKeys = series.map((s) => s.key);
    const log = yScale === 'log' ? logScaleTicks(data, seriesKeys) : null;
    const plotData =
      yScale === 'log'
        ? data.map((row) => {
            const next: Record<string, unknown> = { ...row };
            for (const key of seriesKeys) {
              const v = row[key];
              if (typeof v === 'number' && v <= 0) next[key] = null;
            }
            return next;
          })
        : (data as Record<string, unknown>[]);
    const chartProps = {
      data: plotData,
      ...(syncId !== undefined ? { syncId } : {}),
      ...(margin !== undefined ? { margin: { ...margin } } : {}),
      ...(onActiveIndexChange
        ? {
            onMouseMove: (state: { activeTooltipIndex?: number } | undefined) => {
              const i = state?.activeTooltipIndex;
              onActiveIndexChange(typeof i === 'number' ? i : null);
            },
            onMouseLeave: () => onActiveIndexChange(null),
          }
        : {}),
    };
    const xAxisProps = {
      ...(xTicks !== undefined ? { ticks: [...xTicks], interval: 0 as const } : {}),
      ...(showXAxis ? {} : { hide: true }),
    };
    const yAxisProps = {
      ...(log
        ? { scale: 'log' as const, domain: log.domain, ticks: log.ticks, allowDataOverflow: true }
        : {}),
      ...(yTickCount !== undefined ? { tickCount: yTickCount } : {}),
      ...(yAxisWidth !== undefined ? { width: yAxisWidth } : {}),
    };
    const tooltipContent = showTooltip ? {} : { content: () => null };

    switch (kind) {
      case 'line':
        return (
          <LineChart {...chartProps}>
            {showGrid ? <CartesianGrid stroke={gridStroke} vertical={false} /> : null}
            <XAxis dataKey={xKey} {...axisStyle} {...xAxisFormat} {...xAxisProps} />
            <YAxis {...axisStyle} {...yAxisFormat} {...yAxisProps} />
            <RechartsTooltip
              contentStyle={tooltipStyle}
              cursor={{ stroke: cursorStroke }}
              {...tooltipFormat}
              {...tooltipContent}
            />
            {showLegend ? <Legend wrapperStyle={{ fontSize: 12 }} /> : null}
            {series.map((s, i) => (
              <Line
                key={s.key}
                dataKey={s.key}
                name={s.label ?? s.key}
                type="monotone"
                stroke={colourFor(i, s.colorVar)}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        );
      case 'bar':
        return (
          <BarChart {...chartProps}>
            {showGrid ? <CartesianGrid stroke={gridStroke} vertical={false} /> : null}
            <XAxis dataKey={xKey} {...axisStyle} {...xAxisFormat} {...xAxisProps} />
            <YAxis {...axisStyle} {...yAxisFormat} {...yAxisProps} />
            <RechartsTooltip
              contentStyle={tooltipStyle}
              cursor={{ fill: 'var(--color-surface-2)' }}
              {...tooltipFormat}
              {...tooltipContent}
            />
            {showLegend ? <Legend wrapperStyle={{ fontSize: 12 }} /> : null}
            {series.map((s, i) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label ?? s.key}
                fill={colourFor(i, s.colorVar)}
                // Stacked segments stay square; only a lone bar gets rounded ends.
                radius={stacked ? [0, 0, 0, 0] : [4, 4, 0, 0]}
                {...stackId}
              />
            ))}
          </BarChart>
        );
      case 'area':
        return (
          <AreaChart {...chartProps}>
            {showGrid ? <CartesianGrid stroke={gridStroke} vertical={false} /> : null}
            <XAxis dataKey={xKey} {...axisStyle} {...xAxisFormat} {...xAxisProps} />
            <YAxis {...axisStyle} {...yAxisFormat} {...yAxisProps} />
            <RechartsTooltip
              contentStyle={tooltipStyle}
              cursor={{ stroke: cursorStroke }}
              {...tooltipFormat}
              {...tooltipContent}
            />
            {showLegend ? <Legend wrapperStyle={{ fontSize: 12 }} /> : null}
            {series.map((s, i) => (
              <Area
                key={s.key}
                dataKey={s.key}
                name={s.label ?? s.key}
                type="monotone"
                stroke={colourFor(i, s.colorVar)}
                fill={colourFor(i, s.colorVar)}
                fillOpacity={0.18}
                strokeWidth={2}
                {...stackId}
              />
            ))}
          </AreaChart>
        );
      case 'donut': {
        const dataKey = series[0]?.key ?? 'value';
        return (
          <PieChart>
            <RechartsTooltip contentStyle={tooltipStyle} />
            {showLegend ? <Legend wrapperStyle={{ fontSize: 12 }} /> : null}
            <Pie
              data={data as Record<string, unknown>[]}
              dataKey={dataKey}
              nameKey={xKey}
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="80%"
              paddingAngle={2}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={colourFor(i)} stroke="var(--color-surface)" />
              ))}
            </Pie>
          </PieChart>
        );
      }
      case 'funnel': {
        const dataKey = series[0]?.key ?? 'value';
        return (
          <FunnelChart>
            <RechartsTooltip contentStyle={tooltipStyle} />
            <Funnel
              data={data as Record<string, unknown>[]}
              dataKey={dataKey}
              isAnimationActive={false}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={colourFor(i)} />
              ))}
              <LabelList dataKey={xKey} position="inside" fill="var(--color-accent-ink)" />
            </Funnel>
          </FunnelChart>
        );
      }
    }
  }
}
